<script setup lang="ts">
/**
 * PrinterSelect
 *
 * Shared presentational component that renders the printer dropdown
 * with a label, loading/disabled states, and a discovery-error message.
 *
 * Designed to pair with the `usePrinterSelect()` composable so every
 * print dialog no longer duplicates the same Select+error block.
 *
 * Usage:
 *   <PrinterSelect
 *     v-model="selectedPrinter"
 *     :options="printerOptions"
 *     :is-discovering="isDiscovering"
 *     :discover-error="discoverError"
 *   />
 */
import Select from 'primevue/select';
import type { PrinterOption } from '@/shared/composables/usePrinterDiscovery';

defineProps<{
  /** Dropdown-friendly printer options from `usePrinterSelect().printerOptions`. */
  options: PrinterOption[];
  /** Whether printer discovery is in-flight. */
  isDiscovering: boolean;
  /** Last discovery error message (shown as red helper text). */
  discoverError: string | null;
}>();

const selectedPrinter = defineModel<PrinterOption | null>({ required: true });
</script>

<template>
  <div class="flex flex-col gap-1">
    <label class="text-sm font-medium text-heading">
      {{ $t('printCollection.selectPrinter') }}
    </label>
    <Select
      v-model="selectedPrinter"
      :options="options"
      option-label="label"
      :placeholder="isDiscovering ? $t('common.loading') : $t('milk.selectPrinter')"
      :loading="isDiscovering"
      :disabled="isDiscovering"
      class="w-full"
    />
    <small
      v-if="discoverError"
      class="text-red-500 text-xs mt-1"
    >{{ discoverError }}</small>
  </div>
</template>
